import React, { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";

// Utilities
import moment from "moment";

// Contexts
import { AuthenticatedUserContext } from "../contexts/AuthenticatedUserContext";

// Components
import { Device, DeviceInfo } from "@capacitor/device";
import { IonBadge, IonContent, IonLabel, IonPage, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route } from "react-router-dom";

/* Listeners */
import AppUrlListener from "../listeners/AppUrlListener";
// Pages
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TypingIndicatorType } from "@wearesweet/sendbird-uikit-react";
import SendbirdProvider from "@wearesweet/sendbird-uikit-react/SendbirdProvider";
import { debug } from "../App";
import { config } from "../app.config";
import { AdminPanel } from "../components/Admin/Panel/AdminPanel";
import { SignOutAlert } from "../components/SignOutAlert";
import { useApplication } from "../contexts/ApplicationContext";
import { useSettings } from "../contexts/SettingsContext";
import { Account } from "../pages/Account/Account";
import AdminMembersIndex from "../pages/Admin/Members/AdminMembersIndex";
import Registration from "../pages/Admin/Registration/Registration";
import Settings from "../pages/Admin/Settings/Settings";
import { Channel } from "../pages/Chat/Channel";
import { ChannelSettings } from "../pages/Chat/ChannelSettings";
import Chat from "../pages/Chat/Chat";
import { SupportRequestSent } from "../pages/Chat/components/SupportRequestSent";
import { Mchat } from "../pages/Chat/Mchat";
import { MessageSearch } from "../pages/Chat/mobile/MessageSearch";
import ContactCategories from "../pages/Contact/ContactCategories";
import ContactForm from "../pages/Contact/ContactForm";
import Events from "../pages/Events/index";
import { MyFiles } from "../pages/MyFiles/MyFiles";
import { List } from "../pages/News/List";
import { View } from "../pages/News/View";
import Profile from "../pages/Profile/Profile";
import { Resources } from "../pages/Resources";
import { File } from "../pages/Resources/File";
import SignOut from "../pages/SignOut/SignOut";
import { NavItems } from "../utils";
import { useViewport } from "../utils/useViewport";

export const AuthenticatedRouter = (props: any) => {
  const { isMobile } = useViewport();
  const { member, numUnreadFiles } = useContext(AuthenticatedUserContext);
  const isApproved = moment(member?.["approved_at"]).isBefore();
  const isAdmin = member?.is_admin;
  const [, setDevice]: [DeviceInfo | any, Dispatch<SetStateAction<any>>] = useState(null);
  const { application } = useApplication();
  const router = useRef<IonReactRouter>(null);
  const navItems = NavItems();
  const { settings } = useSettings();

  useEffect(() => {
    Device.getInfo().then((device) => setDevice(device));
  }, []);

  useEffect(() => {
    document.body.classList.add("loggedin");

    return () => {
      document.body.classList.remove("loggedin");
    };
  }, []);

  return (
    <IonReactRouter ref={router}>
      <SendbirdProvider
        appId={application.sendbird_app_id} // Specify your Sendbird application ID.
        userId={member?.id + ""}
        accessToken={member?.sendbird_token}
        colorSet={{
          "--sendbird-light-onlight-03": "rgb(0, 0, 0, 0.64)",
          "--sendbird-light-primary-300": "#333",
          "--sendbird-light-primary-400": "#333",
          "--sendbird-light-primary-100": "rgba(0, 0, 0, 0.25)",
        }}
        stringSet={{
          // Sendbird-specific
          CHANNEL_SETTING__HEADER__TITLE: `${settings?.global_wording.misc_channel} settings`,
          CHANNEL_SETTING__MODERATION__REGISTER_AS_OPERATOR: `Make group admin`,
          CHANNEL_SETTING__MODERATION__UNREGISTER_OPERATOR: `Demote from group admin`,
          CHANNEL_SETTING__MEMBERS__TITLE: `${settings?.global_wording.entity_members}s`,
          CHANNEL_SETTING__MEMBERS__SEE_ALL_MEMBERS: `All ${settings?.global_wording.entity_members.toString().toLowerCase()}s`,
          CHANNEL_SETTING__MEMBERS__INVITE_MEMBER: `Add ${settings?.global_wording.entity_members.toString().toLowerCase()}`,
          CHANNEL_SETTING__MEMBERS__SELECT_TITLE: `Select ${settings?.global_wording.entity_members.toString().toLowerCase()}s`,
          CHANNEL_SETTING__MEMBERS__OPERATOR: `Group admin`,
          CHANNEL_SETTING__LEAVE_CHANNEL__TITLE: `Leave ${settings?.global_wording.misc_channel.toString().toLowerCase()}`,
          CHANNEL_SETTING__OPERATORS__TITLE: `Group admins`,
          CHANNEL_SETTING__OPERATORS__TITLE_ADD: `Add group admin`,
          CHANNEL_SETTING__MUTED_MEMBERS__TITLE: `Muted ${settings?.global_wording.entity_members.toString().toLowerCase()}s`,
          CHANNEL_SETTING__MUTED_MEMBERS__TITLE_ALL: `All muted ${settings?.global_wording.entity_members.toString().toLowerCase()}s`,
          CHANNEL_SETTING__NO_UNMUTED: `No muted ${settings?.global_wording.entity_members.toString().toLowerCase()} yet`,
          CHANNEL_SETTING__BANNED_MEMBERS__TITLE: `Banned ${settings?.global_wording.entity_members.toString().toLowerCase()}s`,
          CHANNEL_SETTING__FREEZE_CHANNEL: `Freeze ${settings?.global_wording.misc_channel.toString().toLowerCase()}`,
          CHANNEL_SETTING__MODERATION__EMPTY_BAN: `No banned ${settings?.global_wording.entity_members.toString().toLowerCase()}s yet`,
          CHANNEL_SETTING__MODERATION__ALL_BAN: `All banned ${settings?.global_wording.entity_members.toString().toLowerCase()}s`,
          OPEN_CHANNEL_LIST__TITLE: `${settings?.global_wording.misc_channel}s`,
          CREATE_OPEN_CHANNEL_LIST__TITLE: `New ${settings?.global_wording.misc_channel.toString().toLowerCase()} profile`,
          CREATE_OPEN_CHANNEL_LIST__SUBTITLE__TEXT_SECTION: `${settings?.global_wording.misc_channel} name`,
          CREATE_OPEN_CHANNEL_LIST__SUBTITLE__TEXT_PLACE_HOLDER: `Enter ${settings?.global_wording.misc_channel.toString().toLowerCase()} name`,
          OPEN_CHANNEL_SETTINGS__OPERATOR_TITLE: `${settings?.global_wording.misc_channel} information`,
          OPEN_CHANNEL_SETTINGS__DELETE_CHANNEL_PANEL: `Delete ${settings?.global_wording.misc_channel.toString().toLowerCase()}`,
          OPEN_CHANNEL_SETTINGS__DELETE_CHANNEL_TITLE: `Delete ${settings?.global_wording.misc_channel.toString().toLowerCase()}?`,
          OPEN_CHANNEL_SETTINGS__DELETE_CHANNEL_CONTEXT: `Once deleted, this ${settings?.global_wording.misc_channel} cannot be restored.`,
          OPEN_CHANNEL_SETTINGS__OPERATORS_TITLE: `Group admins`,
          OPEN_CHANNEL_SETTINGS__OPERATORS__TITLE_ADD: `Add group admin`,
          OPEN_CHANNEL_SETTINGS__BANNED_MEMBERS__TITLE: `Banned ${settings?.global_wording.entity_members.toString().toLowerCase()}s`,
          OPEN_CHANNEL_SETTINGS__BANNED_MEMBERS__TITLE_ALL: `All banned ${settings?.global_wording.entity_members.toString().toLowerCase()}s`,
          OPEN_CHANNEL_SETTINGS__BANNED_MEMBERS__NO_ONE: `No banned ${settings?.global_wording.entity_members.toString().toLowerCase()} yet`,
          OPEN_CHANNEL_SETTINGS__MEMBERS__OPERATOR: `Group admin`,
          OPEN_CHANNEL_SETTING__MODERATION__REGISTER_AS_OPERATOR: `Make group admin`,
          OPEN_CHANNEL_SETTING__MODERATION__UNREGISTER_OPERATOR: `Demote from group admin`,
          CHANNEL_FROZEN: `${settings?.global_wording.misc_channel} frozen`,
          PLACE_HOLDER__NO_CHANNEL: `No ${settings?.global_wording.misc_channel.toString().toLowerCase()}s`,
          MODAL__CHANNEL_INFORMATION__TITLE: `Edit ${settings?.global_wording.misc_channel.toString().toLowerCase()} information`,
          MODAL__CHANNEL_INFORMATION__CHANNEL_IMAGE: `${settings?.global_wording.misc_channel.toString()} image`,
          MODAL__CHANNEL_INFORMATION__CHANNEL_NAME: `${settings?.global_wording.misc_channel.toString()} name`,
          MODAL__CHOOSE_CHANNEL_TYPE__TITLE: `New ${settings?.global_wording.misc_channel.toString().toLowerCase()}`,
          MODAL__CREATE_CHANNEL__TITLE: `New ${settings?.global_wording.misc_channel.toString().toLowerCase()}`,
          MODAL__LEAVE_CHANNEL__TITLE: `Leave this ${settings?.global_wording.misc_channel.toString().toLowerCase()}?`,
          MODAL__VOICE_MESSAGE_INPUT_DISABLED__TITLE_MUTED: `You are muted by the group admin`,
          MODAL__VOICE_MESSAGE_INPUT_DISABLED__TITLE_FROZEN: `${settings?.global_wording.misc_channel} is frozen`,
          MESSAGE_INPUT__PLACE_HOLDER__DISABLED: `Chat is unavailable in this ${settings?.global_wording.misc_channel.toString().toLowerCase()}`,
          MESSAGE_MENU__OPEN_IN_CHANNEL: `Open in ${settings?.global_wording.misc_channel.toString().toLowerCase()}`,
          SEARCH_IN_CHANNEL: `Search in ${settings?.global_wording.misc_channel.toString().toLowerCase()}`,
        }}
        breakpoint={isMobile}
        //isMentionEnabled={true}
        //isVoiceMessageEnabled={true}
        //isTypingIndicatorEnabledOnChannelList={true}
        //isMessageReceiptStatusEnabledOnChannelList={true}
        uikitOptions={{
          groupChannel: {
            typingIndicatorTypes: new Set([TypingIndicatorType.Bubble, TypingIndicatorType.Text]),
            enableMention: true,
            enableVoiceMessage: true,
            enableTypingIndicator: true,
          },
        }}
        config={{
          logLevel: config.debug_step ? "all" : "none",
        }}
      >
        <>
          <AppUrlListener />
          {/*<MobileNav />*/}
          <SignOutAlert />

          <IonTabs>
            <IonRouterOutlet id="main">
              <Redirect from="/" to={isApproved ? "/chat" : "/profile"} exact />

              {/* Register*/}
              <Redirect from="/register" to={"/"} />

              {/* Profile */}
              <Route path="/profile" component={Profile} exact />

              {/* Account */}
              <Route path="/account" component={Account} exact />

              {/* My Files */}
              <Route path={"/my-files"} component={MyFiles} exact />

              {/* Sign out */}
              <Route path="/sign-out" component={SignOut} exact />

              {/* Chat */}
              <Route path="/chat" render={(props) => <Chat {...props} />} />

              {/* Mobile Chat */}
              <Route path="/mchat" exact component={Mchat} />

              <Route
                path="/mchat/channels/:channelUrl"
                exact
                render={(props) => {
                  debug("AuthenticatedRouter", "mchat", "channelUrl", props);
                  return (
                    <IonPage>
                      <Channel {...props} />
                    </IonPage>
                  );
                }}
              />

              <Route
                path="/mchat/channels/:channelUrl/highlight/:highlightMessageId"
                exact
                render={(props) => {
                  debug("AuthenticatedRouter", "mchat", "highlightMessageId", props);
                  return (
                    <IonPage>
                      <Channel {...props} />
                    </IonPage>
                  );
                }}
              />

              <Route
                path="/mchat/channels/:channelUrl/settings"
                exact
                render={(props) => {
                  debug("AuthenticatedRouter", "mchat", "channelSettings", props);
                  return (
                    <IonPage>
                      <IonContent fullscreen={true}>
                        <ChannelSettings channelUrl={props.match.params.channelUrl} />
                      </IonContent>
                    </IonPage>
                  );
                }}
              />

              <Route
                path="/mchat/channels/:channelUrl/from-contact-us"
                exact
                render={(props) => {
                  return (
                    <IonPage>
                      <SupportRequestSent />
                      <Channel {...props} />
                    </IonPage>
                  );
                }}
              />

              <Route
                path="/mchat/channels/:channelUrl/search"
                exact
                render={(props) => {
                  return <MessageSearch {...props} />;
                }}
              />

              {/* News */}
              <Route path="/news" render={(props) => isApproved && <List {...props} />} exact />
              <Route path="/news/:id" render={(props) => isApproved && <View {...props} />} exact />

              {/* Events */}
              <Route path="/events" component={Events} exact />

              {/* Resources */}
              <Route path="/resources/:folder([0-9]+)?/:name?" render={(props) => <Resources {...props} />} />
              <Route path="/files/:file([0-9]+)?/:title?" render={(props) => <File {...props} />} />

              {/* Contact */}
              <Route path="/contact" render={(props) => isApproved && <ContactCategories {...props} />} exact />
              <Route path="/contact/:id([0-9]+)" render={(props) => isApproved && <ContactForm {...props} />} exact />

              {/* Admin Routes */}
              {isApproved && isAdmin ? (
                <>
                  {/* Members */}
                  <Route path="/admin/members" render={(props) => <AdminMembersIndex {...props} />} exact />
                  <Route path="/admin/members/:member([0-9]+)" render={(props) => <Profile {...props} />} exact />

                  {/* Registration */}
                  <Route path="/admin/register" render={(props) => <Registration {...props} />} exact />

                  {/* Settings */}
                  <Route path="/admin/settings" component={Settings} exact />

                  {/* Laravel Nova Admin Panel */}
                  <Route path="/admin/panel" render={(props) => <AdminPanel {...props} />} exact />
                </>
              ) : (
                <></>
              )}
            </IonRouterOutlet>

            <IonTabBar
              slot="bottom"
              className={"mobile-tab-bar"}
              style={{
                display: isMobile ? "" : "none",
              }}
            >
              {navItems
                .filter((navItem) => navItem.mobile)
                .map((navItem, key) => (
                  <IonTabButton tab={navItem.title} href={navItem.url} key={"navItem-" + key}>
                    <FontAwesomeIcon icon={navItem.icon} />
                    <IonLabel>
                      {navItem.title}
                      {navItem.title === "Profile" && numUnreadFiles > 0 && <IonBadge color={"danger"}>{numUnreadFiles}</IonBadge>}
                    </IonLabel>
                  </IonTabButton>
                ))}
            </IonTabBar>
          </IonTabs>
        </>
      </SendbirdProvider>
    </IonReactRouter>
  );
};
